/**
 * Bedrock Service for Training Module (via Backend API)
 * Handles all LLM operations using AWS Bedrock through backend proxy
 *
 * This is the secure approach - credentials are stored on the backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://server.spatialgrid.ai';
const DEFAULT_MODEL =
  process.env.REACT_APP_BEDROCK_MODEL || 'openai.gpt-oss-120b-1:0';

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token') || '';
};

// Get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Production: retries with exponential backoff and timeout
const DEFAULT_TIMEOUT_MS = 90000; // 90s for LLM calls
const TEST_TIMEOUT_MS = 15000;    // 15s for test
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

async function fetchWithRetry(url, options = {}, { timeoutMs = DEFAULT_TIMEOUT_MS, retries = MAX_RETRIES, retryDelayMs = RETRY_DELAY_MS } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return res;
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;
      const isRetryable = err.name === 'AbortError' || /network|fetch|timeout/i.test(err?.message || '');
      if (attempt < retries && isRetryable) {
        const delay = retryDelayMs * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, delay));
      } else {
        throw err;
      }
    }
  }
  throw lastError;
}

export class OllamaService {
  constructor(model = DEFAULT_MODEL) {
    this.model = model;
  }

  /**
   * Test connection to Bedrock via backend
   */
  async testConnection() {
    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}/api/v1/bedrock/test`,
        { method: 'GET', headers: getAuthHeaders() },
        { timeoutMs: TEST_TIMEOUT_MS, retries: 1 }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          connected: data.data.connected,
          models: [],
          message:
            data.data.message || 'Successfully connected to Amazon Bedrock',
          testResponse: data.data.testResponse,
        };
      }

      return {
        connected: false,
        models: [],
        message: data.message || 'Failed to connect to Bedrock',
        error: data.data?.error || 'Unknown error',
      };
    } catch (error) {
      console.error('Bedrock connection error:', error);
      return {
        connected: false,
        models: [],
        message: `Failed to connect to Bedrock: ${error.message}`,
        error: error.message,
      };
    }
  }

  /**
   * Generate text using Bedrock via backend
   * @param {string} prompt - The prompt to send
   * @param {Object} options - Additional options
   * @returns {Promise<string>} Generated text
   */
  async generate(prompt, options = {}) {
    const {
      model = this.model,
      stream = false,
      temperature = 0.7,
      top_p = 0.9,
      maxTokens = 4096,
      systemPrompt = null,
    } = options;

    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}/api/v1/bedrock/generate`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            prompt,
            model,
            stream,
            temperature,
            top_p,
            maxTokens,
            systemPrompt,
          }),
        },
        { timeoutMs: DEFAULT_TIMEOUT_MS, retries: MAX_RETRIES }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      if (stream) {
        return this.handleStreamResponse(response);
      }

      const data = await response.json();
      return data.data?.text || '';
    } catch (error) {
      console.error('Bedrock generation error:', error);
      throw new Error(`Failed to generate text: ${error.message}`);
    }
  }

  /**
   * Handle streaming response
   */
  async handleStreamResponse(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter((line) => line.trim());

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.text) {
                fullText += data.text;
              }
              if (data.done) {
                return data.fullText || fullText;
              }
            } catch (e) {
              // Skip invalid JSON lines
            }
          }
        }
      }

      return fullText;
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Generate structured JSON response
   * @param {string} prompt - The prompt
   * @param {Object} schema - Expected JSON schema description
   * @returns {Promise<Object>} Parsed JSON response
   */
  async generateJSON(prompt, schema = null) {
    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}/api/v1/bedrock/generate`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            prompt: schema
              ? `${prompt}\n\nRespond with valid JSON only. Expected structure: ${JSON.stringify(schema, null, 2)}`
              : `${prompt}\n\nRespond with valid JSON only. Do not include any markdown formatting, code blocks, or explanations. Return only the JSON object.`,
            schema,
            model: this.model,
            temperature: 0.3,
          }),
        },
        { timeoutMs: DEFAULT_TIMEOUT_MS, retries: MAX_RETRIES }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      const responseText = data.data?.text || '';

      // Try to parse JSON, handling cases where response might have markdown code blocks
      let parsed;
      try {
        // Remove markdown code blocks if present
        let cleanedText = responseText
          .replace(/```json\n?/g, '')
          .replace(/```\n?/g, '')
          .replace(/^[^{]*/, '') // Remove any text before first {
          .trim();

        // Try to find and extract the JSON object
        // Look for the opening brace and try to find matching closing brace
        const firstBrace = cleanedText.indexOf('{');
        if (firstBrace === -1) {
          throw new Error('No JSON object found in response');
        }

        cleanedText = cleanedText.substring(firstBrace);

        // Try to find the matching closing brace
        let braceCount = 0;
        let lastValidIndex = -1;
        for (let i = 0; i < cleanedText.length; i++) {
          if (cleanedText[i] === '{') braceCount++;
          if (cleanedText[i] === '}') {
            braceCount--;
            if (braceCount === 0) {
              lastValidIndex = i;
              break;
            }
          }
        }

        if (lastValidIndex === -1) {
          // If we can't find matching brace, try to fix common issues
          // Remove trailing incomplete content
          const lastBrace = cleanedText.lastIndexOf('}');
          if (lastBrace > 0) {
            cleanedText = cleanedText.substring(0, lastBrace + 1);
          } else {
            throw new Error('Invalid JSON structure - no matching braces');
          }
        } else {
          cleanedText = cleanedText.substring(0, lastValidIndex + 1);
        }

        // Try to fix common JSON issues
        // Remove trailing commas before closing braces/brackets
        cleanedText = cleanedText.replace(/,(\s*[}\]])/g, '$1');

        // Try parsing
        parsed = JSON.parse(cleanedText);

      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        console.error('Parse error message:', parseError.message);
        console.error('Response text (full):', responseText);

        // Try one more time with a more aggressive cleanup
        try {
          // Extract just the JSON part more aggressively
          const jsonStart = responseText.indexOf('{');
          const jsonEnd = responseText.lastIndexOf('}');

          if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
            let extractedJson = responseText.substring(jsonStart, jsonEnd + 1);
            // Remove trailing commas
            extractedJson = extractedJson.replace(/,(\s*[}\]])/g, '$1');
            parsed = JSON.parse(extractedJson);

          } else {
            throw parseError;
          }
        } catch (fallbackError) {
          console.error('Fallback parsing also failed:', fallbackError);
          throw new Error(
            `Invalid JSON response: ${parseError.message}. Response preview: ${responseText.substring(0, 200)}...`
          );
        }
      }

      return parsed || {};
    } catch (error) {
      console.error('Failed to generate JSON:', error);
      throw new Error(`Failed to generate JSON: ${error.message}`);
    }
  }

  /**
   * Process training content through LXD bot
   * @param {string} content - Training content
   * @param {string} botDescription - Bot system prompt
   * @returns {Promise<string>} Processed content
   */
  async processWithLXD(content, botDescription) {
    const prompt = `${botDescription}\n\nTraining Content:\n${content}\n\nPlease analyze and structure this content for VR training.`;
    return await this.generate(prompt, {
      systemPrompt: botDescription,
    });
  }

  /**
   * Generate research structure (chapters) from content - ASSET-DRIVEN
   * @param {string} content - Training content
   * @param {string} botDescription - Researcher bot prompt
   * @param {Array} assets - Array of assets with descriptions and usage info
   * @returns {Promise<Array>} Array of chapters based on asset flow
   */
  async generateChapters(content, botDescription, assets = []) {
    // Prepare detailed asset information
    const mandatoryAssets = assets.filter((a) => a.mandatory);
    const optionalAssets = assets.filter((a) => !a.mandatory);

    const assetsInfo = assets
      .map((asset) => {
        return `Asset: ${asset.name}
- Type: ${asset.type || asset.assetType || 'mesh'}
- Description: ${asset.description || 'No description provided'}
- Usage in Training: ${asset.usage || 'Not specified'}
- Status: ${asset.status || 'needed'}
- Mandatory: ${asset.mandatory ? 'YES' : 'NO'}`;
      })
      .join('\n\n');

    const singleAssetMode = assets.length === 1;
    console.log(`[OllamaService] generateChapters called with ${assets.length} asset(s). Single asset mode: ${singleAssetMode}`);
    if (singleAssetMode) {
      console.log(`[OllamaService] Single asset details:`, assets[0]);
    }

    const prompt = `${botDescription}

Training Document Content:
${content}

AVAILABLE ASSETS FOR THIS TRAINING:
${assetsInfo}

CRITICAL INSTRUCTIONS - ASSET-DRIVEN STORYBOARD GENERATION:
${singleAssetMode ? `*** SINGLE ASSET MODE ACTIVE ***
YOU MUST GENERATE EXACTLY ONE CHAPTER - NO MORE, NO LESS.
Only the following asset is available:
${assets[0].name}

DO NOT CREATE MULTIPLE CHAPTERS.
DO NOT CREATE CHAPTERS FOR ASSETS THAT DON'T EXIST.
DO NOT CREATE PLACEHOLDER CHAPTERS.
DO NOT CREATE GENERIC CHAPTERS.
Generate one focused chapter with moments for this single asset ONLY.

CRITICAL: You MUST return VALID JSON ONLY. Do not include any explanatory text, markdown formatting, or code blocks.
Start your response with { and end with }. No other text before or after the JSON object.
*** END SINGLE ASSET MODE ***

` : ''}1. The storyboard MUST be generated based on the ASSETS provided above
2. Each moment MUST be about interacting with a specific asset
3. Organize chapters around asset groups or asset usage flows
4. Mandatory assets (${mandatoryAssets.length} total) MUST have moments - they are essential
5. Optional assets can be included if they enhance the training
6. Create a logical flow: how the learner will interact with each asset in sequence
7. Each moment's touchObjectQuery MUST reference the asset name or a clear identifier
8. The story should follow the natural progression of using these assets in the training
Chapter Organization:
- Group related assets together in chapters
- Create moments for each asset interaction
- Follow the logical sequence of asset usage from the document
- Mandatory assets should appear early in the training flow

Generate a structured VR experience design following this JSON schema.
Your response must contain ONLY valid JSON — no explanations, no extra text.

For each moment, provide:
- id: unique identifier
- general: name and description (both nonTechnical and technical versions)
- interaction: 
  * touchObjectQuery (MUST match or reference the asset name) - the object identifier for Unity to find
  * interactionType (REQUIRED) - MUST be exactly one of: "pinch", "grab", "twohandgrab", "teleportation", "snapTurn", "poke", "climbing", "pullingLever", "gravityFall"
    - pinch: pinch gesture (zoom, resize) | grab: single-hand grab and hold | twohandgrab: both hands grab
    - teleportation: player teleports to a location | snapTurn: quick directional turn | poke: finger poke or tap
    - climbing: player climbs a surface or object | pullingLever: pull a lever or handle | gravityFall: object or player falls under gravity
  * duration (seconds) - how long the interaction should take
  * required (boolean) - whether this must be completed to proceed
  * grabbableName (for placepoint only) - name of grabbable object
  * disableGrabOnPlace, disablePlacePointOnPlace, disableRigidbodyOnPlace (for placepoint only) - boolean flags

- beforeTouch: Actions that happen BEFORE the interaction
  * voiceOver: Narration/instructions before interaction
  * displayText: Text shown to user before interaction
  * objects: { query: asset identifier, option: "Highlight"|"Enable"|"Disable"|"Spawn", spawn: spawn config }
  * image: Image URL or path to display
  * video: Video URL or path to play
  * metaLayer: Meta layer identifier for overlay effects
  * holoBooks: Holo book content identifier
  * highlights: Highlight effect query
  * player: Player action/state change
  * baseItem: Base item identifier
  * objAnimation: Object animation identifier
  * uiUpdate: UI update configuration
  * timerDelay: Delay duration in seconds (number)
  * animation: Animation identifier
  * stateTable: State table identifier
  * checklistUI: Array of checklist items

- afterTouch: Actions that happen AFTER the interaction
  * voiceOver: Feedback narration after interaction
  * displayText: Confirmation text after interaction
  * visualEffect: Visual effect identifier
  * sfx: Sound effect identifier
  * metaLayer: Meta layer identifier for overlay effects
  * objects: { query: asset identifier, option: "Highlight"|"Enable"|"Disable"|"Spawn", spawn: spawn config }
  * image: Image URL or path to display
  * video: Video URL or path to play
  * holoBooks: Holo book content identifier
  * highlights: Highlight effect query
  * player: Player action/state change
  * baseItem: Base item identifier
  * objAnimation: Object animation identifier
  * uiUpdate: UI update configuration
  * timerDelay: Delay duration in seconds (number)
  * animation: Animation identifier
  * stateTable: State table identifier
  * checklistUI: Array of checklist items

IMPORTANT - ACTION TYPES FOR VR AUTOMATION:
- Use appropriate action types based on training requirements:
  * Voice Over: For narration and instructions (beforeTouch/afterTouch)
  * Text Media: For display text shown to user (beforeTouch/afterTouch)
  * Objects: For highlighting, enabling, disabling, or spawning assets (beforeTouch/afterTouch)
  * Image Media: For displaying images (beforeTouch/afterTouch)
  * Video Media: For playing videos (beforeTouch/afterTouch)
  * Sound Effects: For audio feedback (afterTouch only)
  * Meta Layer: For UI state changes (beforeTouch/afterTouch)
  * Visual Effect: For visual feedback (afterTouch only)
  * Holo Books: For interactive book content (beforeTouch/afterTouch)
  * Highlights: For highlighting important elements (beforeTouch/afterTouch)
  * Player: For player state changes (beforeTouch/afterTouch)
  * Base Item: For base item references (beforeTouch/afterTouch)
  * Obj Animation: For object animations (beforeTouch/afterTouch)
  * UI Update: For UI state changes (beforeTouch/afterTouch)
  * Timer Delay: For timed delays (beforeTouch/afterTouch, number in seconds)
  * Animation: For general animations (beforeTouch/afterTouch)
  * State Table: For state management (beforeTouch/afterTouch)
  * Checklist UI: For checklist items (beforeTouch/afterTouch, array)

INTERACTION TYPES:
- Each moment MUST correspond to interacting with a specific asset from the list above
- The touchObjectQuery MUST reference the asset name (e.g., if asset is "safety_button", use "safety_button" or similar)
- The interactionType MUST be specified based on the training procedure and MUST be exactly one of these enum values:
  "pinch", "grab", "twohandgrab", "teleportation", "snapTurn", "poke", "climbing", "pullingLever", "gravityFall"

  * Use "pinch" for pinch/zoom/resize gestures
  * Use "grab" when the asset must be grabbed with a single hand (one hand grab only)
  * Use "twohandgrab" when both hands are required to grab the asset
  * Use "teleportation" for teleport movement interactions
  * Use "snapTurn" for quick directional turning gestures
  * Use "poke" for poking or tapping actions
  * Use "climbing" for climb-related interactions
  * Use "pullingLever" for lever pull interactions
  * Use "gravityFall" for fall or drop-off interactions
  Do NOT use any other value for interactionType.
- Create moments in the logical order of asset usage from the document
- Mandatory assets MUST have at least one moment each
- Group assets into chapters based on their relationship or usage sequence
- Include relevant action types in beforeTouch and afterTouch based on training needs

Return this structure:

{
  "chapters": [
    {
      "id": "chapter-1",
      "number": 1,
      "title": "Chapter Title (based on asset group or training phase)",
      "moments": [
        {
          "id": "moment-1-1",
          "content": {
            "nonTechnical": {
              "general": {
                "name": "Interact with [Asset Name]",
                "description": "Learner-friendly description of interacting with this specific asset"
              },
              "interaction": {
                "touchObjectQuery": "[asset name or identifier]",
                "interactionType": "pinch|grab|twohandgrab|teleportation|snapTurn|poke|climbing|pullingLever|gravityFall",
                "duration": 0,
                "required": true
              },
              "beforeTouch": {
                "voiceOver": "Instructions for interacting with [Asset Name]",
                "displayText": "Display text about [Asset Name]",
                "objects": { "query": "", "option": "", "spawn": "" },
                "image": "",
                "video": "",
                "metaLayer": "",
                "holoBooks": "",
                "highlights": "",
                "player": "",
                "baseItem": "",
                "objAnimation": "",
                "uiUpdate": "",
                "timerDelay": 0,
                "animation": "",
                "stateTable": "",
                "checklistUI": []
              },
              "afterTouch": {
                "voiceOver": "Feedback after interacting with [Asset Name]",
                "displayText": "Confirmation about [Asset Name]",
                "visualEffect": "",
                "sfx": "",
                "metaLayer": "",
                "objects": { "query": "", "option": "", "spawn": "" },
                "image": "",
                "video": "",
                "holoBooks": "",
                "highlights": "",
                "player": "",
                "baseItem": "",
                "objAnimation": "",
                "uiUpdate": "",
                "timerDelay": 0,
                "animation": "",
                "stateTable": "",
                "checklistUI": []
              }
            },
            "technical": {
              "general": {
                "name": "[TECH] Interact with [Asset Name]",
                "description": "Technical implementation for [Asset Name] interaction"
              },
              "interaction": {
                "touchObjectQuery": "[asset name or identifier]",
                "interactionType": "pinch|grab|twohandgrab|teleportation|snapTurn|poke|climbing|pullingLever|gravityFall",
                "duration": 0,
                "required": true
              },
              "beforeTouch": {
                "voiceOver": "Technical voice over for [Asset Name]",
                "displayText": "Technical display text for [Asset Name]",
                "objects": { "query": "", "option": "", "spawn": "" },
                "image": "",
                "video": "",
                "metaLayer": "",
                "holoBooks": "",
                "highlights": "",
                "player": "",
                "baseItem": "",
                "objAnimation": "",
                "uiUpdate": "",
                "timerDelay": 0,
                "animation": "",
                "stateTable": "",
                "checklistUI": []
              },
              "afterTouch": {
                "voiceOver": "Technical feedback for [Asset Name]",
                "displayText": "Technical confirmation for [Asset Name]",
                "visualEffect": "",
                "sfx": "",
                "metaLayer": "",
                "objects": { "query": "", "option": "", "spawn": "" },
                "image": "",
                "video": "",
                "holoBooks": "",
                "highlights": "",
                "player": "",
                "baseItem": "",
                "objAnimation": "",
                "uiUpdate": "",
                "timerDelay": 0,
                "animation": "",
                "stateTable": "",
                "checklistUI": []
              }
            }
          }
        }
      ]
    }
  ]
}

Remember: Generate moments based on ASSET FLOW, not random content. Each moment should be about a specific asset interaction.`;

    const momentModeSchema = {
      general: {
        name: 'string',
        description: 'string',
      },
      interaction: {
        touchObjectQuery: 'string',
        interactionType: 'string', // pinch, grab, twohandgrab, teleportation, snapTurn, poke, climbing, pullingLever, gravityFall
        duration: 'number',
        required: 'boolean',
        grabbableName: 'string', // for placepoint
        disableGrabOnPlace: 'boolean', // for placepoint
        disablePlacePointOnPlace: 'boolean', // for placepoint
        disableRigidbodyOnPlace: 'boolean', // for placepoint
      },
      beforeTouch: {
        voiceOver: 'string',
        displayText: 'string',
        objects: {
          query: 'string',
          option: 'string', // Highlight, Enable, Disable, Spawn
          spawn: 'string',
        },
        image: 'string',
        video: 'string',
        metaLayer: 'string',
        holoBooks: 'string',
        highlights: 'string',
        player: 'string',
        baseItem: 'string',
        objAnimation: 'string',
        uiUpdate: 'string',
        timerDelay: 'number',
        animation: 'string',
        stateTable: 'string',
        checklistUI: ['string'],
      },
      afterTouch: {
        voiceOver: 'string',
        displayText: 'string',
        visualEffect: 'string',
        sfx: 'string',
        metaLayer: 'string',
        objects: {
          query: 'string',
          option: 'string', // Highlight, Enable, Disable, Spawn
          spawn: 'string',
        },
        image: 'string',
        video: 'string',
        holoBooks: 'string',
        highlights: 'string',
        player: 'string',
        baseItem: 'string',
        objAnimation: 'string',
        uiUpdate: 'string',
        timerDelay: 'number',
        animation: 'string',
        stateTable: 'string',
        checklistUI: ['string'],
      },
    };

    try {
      const result = await this.generateJSON(prompt, {
        chapters: [
          {
            id: 'string',
            number: 'number',
            title: 'string',
            moments: [
              {
                id: 'string',
                content: {
                  nonTechnical: momentModeSchema,
                  technical: momentModeSchema,
                },
              },
            ],
          },
        ],
      });

      // Add default values for removed properties
      let chaptersWithDefaults = (result.chapters || []).map((chapter) => ({
        ...chapter,
        moments: chapter.moments.map((moment) => ({
          ...moment,
          content: {
            nonTechnical: this.addDefaultValues(moment.content.nonTechnical),
            technical: this.addDefaultValues(moment.content.technical),
          },
        })),
      }));

      console.log(`[OllamaService] Generated ${chaptersWithDefaults.length} chapter(s). Single asset mode: ${singleAssetMode}`);

      // SINGLE ASSET MODE: Consolidate all chapters into one
      if (singleAssetMode && chaptersWithDefaults.length > 1) {
        console.log(`[OllamaService] ✓ CONSOLIDATING: Merging ${chaptersWithDefaults.length} chapters into 1`);
        
        // Combine all moments from all chapters into the first chapter
        const allMoments = chaptersWithDefaults.flatMap((ch) => ch.moments);
        console.log(`[OllamaService] Total moments after merge: ${allMoments.length}`);
        
        const consolidatedChapter = {
          id: 'chapter-1',
          number: 1,
          title: chaptersWithDefaults[0].title || `Training with ${assets[0].name}`,
          moments: allMoments,
        };
        
        chaptersWithDefaults = [consolidatedChapter];
      } else if (singleAssetMode) {
        console.log(`[OllamaService] ✓ Single asset mode but only 1 chapter generated - all good!`);
      }

      return chaptersWithDefaults;
    } catch (error) {
      console.error('Failed to generate chapters:', error);
      return [];
    }
  }

  addDefaultValues(momentContent) {
    return {
      overview: {
        touchObjectQuery: momentContent.interaction?.touchObjectQuery || '',
        voiceOver: momentContent.beforeTouch?.voiceOver || '',
        objects: '',
        displayText: momentContent.beforeTouch?.displayText || '',
        metaLayer: '',
        beforeTouch: {
          description: momentContent.beforeTouch?.displayText || '',
        },
        afterTouch: {
          description: momentContent.afterTouch?.displayText || '',
        },
      },
      general: momentContent.general || { name: '', description: '' },
      interaction: {
        touchObjectQuery: momentContent.interaction?.touchObjectQuery || '',
        interactionType: momentContent.interaction?.interactionType || 'poke',
        duration: momentContent.interaction?.duration || 0,
        required:
          momentContent.interaction?.required !== undefined
            ? momentContent.interaction.required
            : true,
        grabbableName: momentContent.interaction?.grabbableName || '',
        disableGrabOnPlace:
          momentContent.interaction?.disableGrabOnPlace || false,
        disablePlacePointOnPlace:
          momentContent.interaction?.disablePlacePointOnPlace || false,
        disableRigidbodyOnPlace:
          momentContent.interaction?.disableRigidbodyOnPlace || false,
      },
      beforeTouch: {
        objects: {
          query: momentContent.beforeTouch?.objects?.query || '',
          option: momentContent.beforeTouch?.objects?.option || '',
          spawn: momentContent.beforeTouch?.objects?.spawn || '',
        },
        voiceOver: momentContent.beforeTouch?.voiceOver || '',
        displayText: momentContent.beforeTouch?.displayText || '',
        image: momentContent.beforeTouch?.image || '',
        video: momentContent.beforeTouch?.video || '',
        metaLayer: momentContent.beforeTouch?.metaLayer || '',
        holoBooks: momentContent.beforeTouch?.holoBooks || '',
        highlights: momentContent.beforeTouch?.highlights || '',
        player: momentContent.beforeTouch?.player || '',
        baseItem: momentContent.beforeTouch?.baseItem || '',
        objAnimation: momentContent.beforeTouch?.objAnimation || '',
        uiUpdate: momentContent.beforeTouch?.uiUpdate || '',
        timerDelay: momentContent.beforeTouch?.timerDelay || 0,
        animation: momentContent.beforeTouch?.animation || '',
        stateTable: momentContent.beforeTouch?.stateTable || '',
        checklistUI: momentContent.beforeTouch?.checklistUI || [],
      },
      afterTouch: {
        sfx: momentContent.afterTouch?.sfx || '',
        metaLayer: momentContent.afterTouch?.metaLayer || '',
        objects: {
          query: momentContent.afterTouch?.objects?.query || '',
          option: momentContent.afterTouch?.objects?.option || '',
          spawn: momentContent.afterTouch?.objects?.spawn || '',
        },
        voiceOver: momentContent.afterTouch?.voiceOver || '',
        displayText: momentContent.afterTouch?.displayText || '',
        image: momentContent.afterTouch?.image || '',
        video: momentContent.afterTouch?.video || '',
        visualEffect: momentContent.afterTouch?.visualEffect || '',
        holoBooks: momentContent.afterTouch?.holoBooks || '',
        highlights: momentContent.afterTouch?.highlights || '',
        player: momentContent.afterTouch?.player || '',
        baseItem: momentContent.afterTouch?.baseItem || '',
        objAnimation: momentContent.afterTouch?.objAnimation || '',
        uiUpdate: momentContent.afterTouch?.uiUpdate || '',
        timerDelay: momentContent.afterTouch?.timerDelay || 0,
        animation: momentContent.afterTouch?.animation || '',
        stateTable: momentContent.afterTouch?.stateTable || '',
        checklistUI: momentContent.afterTouch?.checklistUI || [],
      },
      voiceWarnings: [],
      notes: '',
    };
  }

  /**
   * Generate concept design for a chapter
   * @param {string} chapterContent - Chapter content
   * @param {string} botDescription - Concept Designer bot prompt
   * 
   * 
   *
   * @returns {Promise<Object>} Detailed chapter design
   */
  async generateConceptDesign(chapterContent, botDescription) {
    const prompt = `${botDescription}\n\nChapter Content:\n${chapterContent}\n\nConvert this chapter into a precise, moment-by-moment VR design.`;
    return await this.generate(prompt, {
      systemPrompt: botDescription,
    });
  }

  /**
   * Generate moment details
   * @param {string} momentDesign - Moment design description
   * @param {string} botDescription - Moment Writer bot prompt
   * @returns {Promise<Object>} Complete moment block
   */
  async generateMomentDetails(momentDesign, botDescription) {
    const prompt = `${botDescription}\n\nMoment Design:\n${momentDesign}\n\nGenerate the complete interaction block for this moment.`;
    return await this.generate(prompt, {
      systemPrompt: botDescription,
    });
  }

  /**
   * Analyze content and suggest required assets with categorization
   * @param {string} content - Training content
   * @param {Array} availableAssets - List of available assets
   * @returns {Promise<Array>} Suggested assets with mandatory/non-mandatory categorization
   */
  async suggestRequiredAssets(content, availableAssets = []) {
    const assetsList = availableAssets.map((a) => a.name).join(', ');
    const prompt = `You are designing a VR training experience based on the document below. Your job is to propose the 3D assets, images, videos, and audio files that would make this content engaging and effective in VR.

Training Document Content:
${content}

${assetsList ? `Currently Available Assets in Scene: ${assetsList}` : 'No assets available in the scene yet.'}


Instructions:
1. Read through the document carefully to understand the topic, theme, and learning goals
2. Propose specific objects, equipment, tools, environments, or visual elements that would help a learner experience this content in VR
3. You may infer and suggest assets that are implied by the topic even if not explicitly named — be creative but stay relevant to the subject matter
4. Prioritize the MOST IMPORTANT assets first (mandatory assets should be listed first)
5. Return a MAXIMUM of 20 assets - if there are more, select the 20 most useful ones
6. For each asset, determine:
   - Name: A clear, descriptive asset name (e.g., "laptop", "whiteboard", "server_rack")
   - Type: mesh (3D model), image, video, or audio
   - Mandatory: true if the asset is essential for the training, false if optional
   - Description: What this asset is and why it fits the training
   - Usage: How this asset will be used in the VR training
   - Status: "available" if it matches an existing asset name, "needed" if it needs to be provided
   - Category: Classify the asset type for smart positioning:
     * "safety_equipment" - PPE, safety gear, emergency equipment
     * "wall_mounted" - control panels, buttons, switches, posters, signs, screens
     * "floor_equipment" - large machines, heavy equipment, floor-standing items
     * "handheld_tool" - small tools, instruments, portable equipment, devices
     * "workbench_item" - parts, materials, small equipment on tables
     * "material" - raw materials, supplies, consumables
   - Placement Context: Spatial information for intelligent positioning:
     * surface: "table" | "floor" | "wall" | "hanging" | "freestanding" | "shelf"
     * height_preference: "ground" | "ankle" | "knee" | "waist" | "chest" | "eye_level" | "overhead" | "ceiling"
     * proximity_to: Array of asset names that should be nearby (e.g., ["laptop", "notebook"])
     * workflow_order: Number 1-20 indicating sequence in the experience (1 = first, 20 = last)

7. LIMIT YOUR RESPONSE TO MAXIMUM 20 ASSETS - prioritize mandatory and most important ones
8. Always return at least a few useful assets — even abstract/theoretical documents can be visualized with relevant props (books, screens, diagrams, environments, ambient audio, etc.)

Return a JSON array with this structure (MAXIMUM 20 items):
{
  "assets": [
    {
      "name": "descriptive asset name",
      "type": "mesh|image|video|audio",
      "mandatory": true|false,
      "description": "What this asset is and why it fits the training",
      "status": "available|needed",
      "usage": "How this asset will be used in VR training",
      "category": "safety_equipment|wall_mounted|floor_equipment|handheld_tool|workbench_item|material",
      "placement_context": {
        "surface": "table|floor|wall|hanging|freestanding|shelf",
        "height_preference": "ground|ankle|knee|waist|chest|eye_level|overhead|ceiling",
        "proximity_to": ["related_asset_1", "related_asset_2"],
        "workflow_order": 1
      }
    }
  ]
}

Return a maximum of 20 assets that best support the VR training experience for this content.`;

    try {
      // Validate content is provided
      if (!content || content.trim().length < 50) {
        console.warn(
          'Content too short for asset analysis:',
          content?.length || 0
        );
        return [];
      }

      const result = await this.generateJSON(prompt, {
        assets: [
          {
            name: 'string',
            type: 'string',
            mandatory: 'boolean',
            description: 'string',
            status: 'string',
            usage: 'string',
            category: 'string',
            placement_context: {
              surface: 'string',
              height_preference: 'string',
              proximity_to: ['string'],
              workflow_order: 'number'
            }
          },
        ],
      });

      // Handle different possible response structures
      let assets = [];
      if (result.assets && Array.isArray(result.assets)) {
        assets = result.assets;
      } else if (Array.isArray(result)) {
        // Sometimes the API might return the array directly
        assets = result;
      } else if (result.data && Array.isArray(result.data)) {
        assets = result.data;
      } else if (
        result.data &&
        result.data.assets &&
        Array.isArray(result.data.assets)
      ) {
        assets = result.data.assets;
      }




      // Validate assets structure and limit to 20
      if (assets.length > 0) {

        const validAssets = assets.filter((asset) => asset && asset.name);


        // Limit to maximum 20 assets - prioritize mandatory ones
        if (validAssets.length > 20) {
          const mandatoryAssets = validAssets.filter(
            (a) => a.mandatory === true
          );
          const optionalAssets = validAssets.filter(
            (a) => a.mandatory !== true
          );

          // Take all mandatory assets (up to 20), then fill remaining with optional
          const limitedAssets = [
            ...mandatoryAssets.slice(0, 20),
            ...optionalAssets.slice(0, 20 - mandatoryAssets.length),
          ].slice(0, 20);

          return limitedAssets;
        }

        return validAssets;
      }

      return assets;
    } catch (error) {
      console.error('Failed to suggest assets:', error);
      console.error('Error details:', error.message);
      return [];
    }
  }

  /**
   * Get AI-suggested search keywords for finding a 3D model on Sketchfab.
   * Used at asset selection step so we search with relevant phrases (e.g. "bahubali statue" → ["bahubali", "bahubali statue", "bahubali film"]).
   * @param {string} assetName - Name of the asset (e.g. "bahubali statue")
   * @param {string} description - Optional description
   * @param {string} usage - Optional usage context
   * @returns {Promise<string[]>} Array of 5–10 search keywords/phrases to try on Sketchfab
   */
  async getSketchfabSearchKeywords(assetName, description = '', usage = '') {
    const prompt = `You are helping to find a 3D model on Sketchfab (a 3D model library).

Asset to search for: "${assetName}"
${description ? `Description: ${description}` : ''}
${usage ? `Usage context: ${usage}` : ''}

Return 5 to 10 search keywords or short phrases that would best find this asset on Sketchfab. Include:
- The exact asset name or its main parts (e.g. for "bahubali statue" include "bahubali", "bahubali statue")
- Alternative names, spellings, or cultural/context terms that might be used in 3D model titles or tags
- Short phrases (2–4 words) that describe the same object
- Do NOT add generic words like "3d" or "model" to every keyword—only when it helps (e.g. "bahubali 3d")
- Prefer specific over generic so we get the right asset (e.g. "bahubali" is better than just "statue")

Return ONLY a JSON object with a single key "keywords" whose value is an array of strings. Example:
{"keywords": ["bahubali", "bahubali statue", "bahubali film", "prabhas statue"]}`;

    try {
      const result = await this.generateJSON(prompt, null);

      const keywords = result?.keywords;
      if (Array.isArray(keywords) && keywords.length > 0) {
        const cleaned = keywords
          .filter((k) => typeof k === 'string' && k.trim().length > 0)
          .map((k) => k.trim())
          .slice(0, 10);
        if (cleaned.length > 0) {
          return cleaned;
        }
      }
    } catch (error) {
      console.warn('[OllamaService] getSketchfabSearchKeywords failed:', error.message);
    }
    return [];
  }

  /**
   * Get expected real-world size of an object in centimeters
   * @param {string} assetName - Name of the asset
   * @param {string} description - Description of the asset
   * @returns {Promise<number>} Size in centimeters (max dimension)
   */
  // async getExpectedSize(assetName, description = '') {
  //   const prompt = `You are an expert at estimating real - world object sizes.

  //     Question: What is the typical maximum dimension(length, width, or height) in centimeters for a "${assetName}" ?
  //       Context / Description : ${description}

  //   Keep in mind:
  //   - Return ONLY the number.No text, no units.
  //   - If it's a building (house, office), typical size is 500-2000 cm.
  //     - If it's a person/character, typical height is 178 cm.
  //       - If it's a furniture, typical size is 50-200 cm.
  //         - If it's a handheld object, typical size is 10-30 cm.

  //   Output format: Just the number.`;

  //   try {
  //     console.log(`[OllamaService] Asking for size of: ${assetName} `);
  //     const response = await this.generate(prompt, {
  //       temperature: 0.1,
  //     });
  //     console.log(`[OllamaService] Raw size response for ${assetName}: `, response);

  //     // Robust parsing: look for the last number in the string (often LLMs explain then give the answer)
  //     // or the first number if it starts with one.
  //     const numbers = response.match(/\d+(\.\d+)?/g);

  //     if (numbers && numbers.length > 0) {
  //       // Use the last number found, as sometimes models say "The size is X" where X is the last part
  //       // But also check if there's a standalone number.
  //       // Let's take the largest number found in the response to be safe, assuming it's discussing the max dimension
  //       // Filter out small numbers that might be versions or list indices
  //       const validNumbers = numbers.map(n => parseFloat(n)).filter(n => n > 0);

  //       if (validNumbers.length > 0) {
  //         // Heuristic: Max dimension is likely what we want if multiple numbers appear (e.g. "10x20x30")
  //         const size = Math.max(...validNumbers);
  //         console.log(`[OllamaService] Parsed size for ${assetName}: `, size);
  //         return size;
  //       }
  //     }

  //     console.warn(`[OllamaService] Could not parse size for ${assetName}, defaulting to 100`);
  //     return 100; // Default to 1m if parsing fails
  //   } catch (error) {
  //     console.error('Failed to get expected size:', error);
  //     return 100; // Default to 1m on error
  //   }
  // }
  async getExpectedSize(assetName, description = '') {
    const prompt = `You are measuring objects from the perspective of a person who is 5 feet 4 inches (162.56 cm) tall.

Asset to measure: "${assetName}"
Additional context: ${description}

Estimate the MAXIMUM dimension (longest of length, width, or height) in centimeters.

Reference scale from your 162 height perspective:
- Things at your knee: ~40cm
- Things at your waist: ~60cm  
- Things at your chest: ~80cm
- Things at your eye level: ~100cm
- Things at your full height: ~160cm
- Things you need to look up at: >180cm

Common object categories:
- Tiny handheld (phone, cup, book): 10-30cm
- Medium handheld (laptop, basketball): 30-60cm
- Waist-high furniture (chair, desk): 70-120cm
- Person-height objects (door, fridge): 180-240cm
- Room-scale (small car, large furniture): 300-600cm
- Small building/structure: 1500-3000cm
- Large building: 5000-10000cm

Rules:
1. Return ONLY a number in centimeters
2. NO explanations, units, or extra text
3. Think about how this object relates to your 178cm body
4. For ambiguous items, estimate the most common variant

Output: [number only]`;

    try {
      console.log(`[OllamaService] Estimating size for: ${assetName}`);
      const response = await this.generate(prompt, {
        temperature: 0.1,
        max_tokens: 10, // Restrict output length
      });
      console.log(`[OllamaService] Raw response for ${assetName}: "${response}"`);

      // Enhanced parsing: extract the first clean number
      const cleaned = response.trim();
      
      // Try to match a standalone number at the start
      const match = cleaned.match(/^(\d+(?:\.\d+)?)/);
      
      if (match) {
        const size = parseFloat(match[1]);
        
        // Sanity check: reasonable range for physical objects
        if (size >= 0.1 && size <= 100000) {
          console.log(`[OllamaService] Parsed size for ${assetName}: ${size}cm`);
          return size;
        }
        
        console.warn(`[OllamaService] Size ${size} out of reasonable range for ${assetName}`);
      }
      
      // Fallback: find any number in the response
      const numbers = response.match(/\d+(?:\.\d+)?/g);
      if (numbers && numbers.length > 0) {
        const size = parseFloat(numbers[0]); // Take first number, not max
        
        if (size >= 0.1 && size <= 100000) {
          console.log(`[OllamaService] Fallback parsed size for ${assetName}: ${size}cm`);
          return size;
        }
      }

      console.warn(`[OllamaService] Could not parse valid size for ${assetName}, defaulting to 100`);
      return 100;
      
    } catch (error) {
      console.error(`[OllamaService] Failed to get expected size for ${assetName}:`, error);
      return 100;
    }
  }
  /**
   * Set model
   */
  setModel(modelId) {
    this.model = modelId;
  }
}

// Export singleton instance
export const ollamaService = new OllamaService();

// Export factory function (maintained for backward compatibility)
export function createOllamaService(model) {
  return new OllamaService(model);
}
